import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Help from 'app/utils/designer/form/settings/common/Help';
import olMap from 'app/containers/Maps/SituationalAwareness/olMapController';
import { get } from 'app/utils/lo/lo';
import { getWMSLayer, LAYERS_NAMES } from 'app/utils/maps/layer/layerUtils';
import genUuidv4 from 'uuid/v4';
import XYZPreview from 'app/containers/Maps/LayerAbout/XYZLayer/XYZPreview';

const MapStyled = styled.div`
    height: 200px;
    cursor: all-scroll;
    background-color: white;
    background-image: linear-gradient(45deg, #b0b0b0 25%, transparent 25%), linear-gradient(-45deg, #b0b0b0 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #b0b0b0 75%), linear-gradient(-45deg, transparent 75%, #b0b0b0 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    margin-bottom: 16px;
`;

export const MapPreview = ({ data }) => {
    const layer = get(data, 'layer');
    const style = get(data, 'style');
    const url = get(data, 'layer_url');
    const { extent } = getWMSLayer(data);

    const [map, setMap] = useState();
    const [initMap, setInitMap] = useState(false);

    const mapId = useMemo(() => genUuidv4(), []);

    useEffect(() => {
        if (!map) {
            const previewMap = new olMap();
            setMap(previewMap);
        } else if (map && !initMap) {
            setInitMap(true);
            map.initEntityAboutMap(mapId);
        }
        if (map && layer && url && extent) {
            map.addWMSPreviewLayer && map.addWMSPreviewLayer({ layer, style, url, extent });
        }
    }, [map, layer, style, initMap, url, extent, mapId]);

    return <MapStyled id={mapId} />;
};

export const isValidUrl = (string) => {
    if (!string) return false;
    try {
        new URL(string);
        return true;
    } catch (err) {
        return false;
    }
};

export const additionalFields = (name, data, manuallyhandleErrors, errorsMap) => {
    switch (name) {
        case 'WMS Layer': {
            const wmsLayer = getWMSLayer(data);
            return [
                {
                    field: 'Layer URL',
                    type: 'text',
                    properties: {
                        label: 'Layer URL',
                        name: 'layer_url',
                    },
                    constraints: { required: true, minLength: 3, maxLength: 300 },
                },
                {
                    field: 'Layer',
                    type: 'typeahead',
                    properties: {
                        label: 'Layer',
                        name: 'layer',
                        options: get(data, 'allWMSLayers', []),
                        isVisible: (data) => data.name && data['layer_url'],
                    },
                    constraints: { required: true },
                },
                {
                    field: 'style',
                    type: 'typeahead',
                    properties: {
                        label: 'Style',
                        name: 'style',
                        options: get(wmsLayer, 'styles', []),
                        isVisible: (data) => wmsLayer?.styles?.length && data.name && data['layer'],
                    },
                    constraints: { required: true },
                },
                {
                    type: 'custom',
                    properties: {
                        label: 'Layer Preview',
                        name: 'MapPreview',
                        Component: (props) => <MapPreview {...props} />,
                        isVisible: (data) => (!wmsLayer?.styles?.length && !data?.style && data?.layer) || (data['style'] && data['layer']),
                    },
                },
            ];
        }
        case LAYERS_NAMES.xyz: {
            return [
                {
                    type: 'text',
                    properties: {
                        label: 'Layer URL',
                        name: 'url',
                        placeholder: 'https://example.com/tiles/{z}/{y}/{x}',
                        error: !!errorsMap?.url,
                        helperText: errorsMap?.url ? 'This is a test error message in case tiles are not loaded from valid URL' : '',
                        help: (
                            <Help message='URL template. Must include {x}, {y} or {-y}, and {z} placeholders. A {?-?} template pattern, for example subdomain{a-f}.domain.com, may be used. To template the API key use {{apiKey}}' />
                        ),
                    },
                    constraints: {
                        required: true,
                        custom: (props) => {
                            if (!isValidUrl(props.url)) {
                                return {
                                    format: {
                                        pattern: /somefaketest/,
                                        message: 'This is not a valid URL.',
                                    },
                                };
                            }
                            return {};
                        },
                    },
                },
                {
                    type: 'text',
                    properties: {
                        label: 'API Key',
                        name: 'apiKey',
                    },
                },
                {
                    type: 'mapProjectionTypeahead',
                    properties: {
                        label: 'Projection',
                        name: 'projection',
                    },
                },
                {
                    type: 'custom',
                    properties: {
                        label: 'Layer Preview',
                        name: 'xyz-preview',
                        Component: (props) => <XYZPreview {...props} />,
                        data: data,
                        isVisible: !!(data?.url && isValidUrl(data?.url)),
                    },
                },
            ].filter(Boolean);
        }
        default:
            return [];
    }
};
